import { z } from 'zod';

const statusSchema = z.enum(['strength', 'dexterity', 'weak', 'vulnerable', 'burning', 'thorns']);

const atomicEffectSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('damage'),
    amount: z.number().int().nonnegative(),
    hits: z.number().int().positive().optional(),
    target: z.enum(['selected', 'all']).optional(),
  }),
  z.object({ kind: z.literal('block'), amount: z.number().int().nonnegative() }),
  z.object({ kind: z.literal('draw'), amount: z.number().int().positive() }),
  z.object({ kind: z.literal('heat'), amount: z.number().int() }),
  z.object({
    kind: z.literal('status'),
    status: statusSchema,
    amount: z.number().int(),
    target: z.enum(['self', 'selected', 'all']),
  }),
  z.object({ kind: z.literal('repair'), amount: z.number().int().positive() }),
]);

const effectSchema = z.union([
  atomicEffectSchema,
  z.object({
    kind: z.literal('if_heat'),
    minimum: z.number().int().min(0).max(6),
    consume: z.number().int().min(0).max(6).optional(),
    effects: z.array(atomicEffectSchema).min(1),
  }),
  z.object({
    kind: z.literal('salvage'),
    effects: z.array(atomicEffectSchema).min(1),
  }),
]);

export const cardDefinitionSchema = z.object({
  id: z.string().regex(/^[a-z0-9_]+$/),
  name: z.string().min(1),
  description: z.string().min(1),
  cost: z.number().int().min(0).max(5),
  type: z.enum(['attack', 'skill', 'power', 'status']),
  rarity: z.enum(['starter', 'common', 'uncommon', 'rare', 'status']),
  target: z.enum(['self', 'enemy', 'all_enemies', 'none']),
  effects: z.array(effectSchema),
  gear: z.object({ maxDurability: z.number().int().positive() }).optional(),
  exhaust: z.boolean().optional(),
  ethereal: z.boolean().optional(),
  unplayable: z.boolean().optional(),
  upgrade: z
    .object({
      name: z.string().min(1).optional(),
      description: z.string().min(1).optional(),
      cost: z.number().int().min(0).max(5).optional(),
      effects: z.array(effectSchema).optional(),
      gear: z.object({ maxDurability: z.number().int().positive() }).optional(),
    })
    .optional(),
});

const enemyPressureSchema = {
  playerHeat: z.number().int().min(0).max(6).optional(),
  gearWearOnHpDamage: z.number().int().positive().optional(),
  statusCard: z.object({
    definitionId: z.string().regex(/^[a-z0-9_]+$/),
    amount: z.number().int().positive(),
  }).optional(),
};

const enemyMoveSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('attack'),
    amount: z.number().int().nonnegative(),
    hits: z.number().int().positive().optional(),
    ...enemyPressureSchema,
  }),
  z.object({ kind: z.literal('block'), amount: z.number().int().nonnegative() }),
  z.object({
    kind: z.literal('attack_status'),
    amount: z.number().int().nonnegative(),
    status: statusSchema,
    statusAmount: z.number().int().positive(),
    ...enemyPressureSchema,
  }),
  z.object({
    kind: z.literal('buff'),
    status: statusSchema,
    amount: z.number().int().positive(),
  }),
]);

export const enemyDefinitionSchema = z.object({
  id: z.string().regex(/^[a-z0-9_]+$/),
  name: z.string().min(1),
  maxHp: z.number().int().positive(),
  description: z.string().min(1),
  pattern: z.array(enemyMoveSchema).min(1),
  phases: z.array(z.object({
    atHp: z.number().int().positive(),
    onEnterBlock: z.number().int().nonnegative().optional(),
    onEnterStrength: z.number().int().nonnegative().optional(),
    pattern: z.array(enemyMoveSchema).min(1),
  })).optional(),
});

export const encounterDefinitionSchema = z.object({
  id: z.string().regex(/^[a-z0-9_]+$/),
  name: z.string().min(1),
  act: z.number().int().min(1).max(3),
  kind: z.enum(['normal', 'elite', 'boss']),
  enemies: z.array(z.string()).min(1).max(3),
});

export const relicDefinitionSchema = z.object({
  id: z.string().regex(/^[a-z0-9_]+$/),
  name: z.string().min(1),
  description: z.string().min(1),
  rarity: z.enum(['starter', 'common', 'elite', 'boss']),
  modifiers: z.object({
    maxHp: z.number().int().optional(),
    maxEnergy: z.number().int().optional(),
    openingDraw: z.number().int().nonnegative().optional(),
    drawPerTurn: z.number().int().nonnegative().optional(),
    startBlock: z.number().int().nonnegative().optional(),
    startHeat: z.number().int().min(0).max(6).optional(),
    startStrength: z.number().int().optional(),
    startThorns: z.number().int().nonnegative().optional(),
    startVulnerable: z.number().int().nonnegative().optional(),
    attackBonus: z.number().int().optional(),
    blockBonus: z.number().int().optional(),
    gearProtectionPerCombat: z.number().int().nonnegative().optional(),
    blockOnGearBreak: z.number().int().nonnegative().optional(),
    rewardIron: z.number().int().optional(),
    healAfterCombat: z.number().int().optional(),
    repairAfterCombat: z.number().int().nonnegative().optional(),
    overheatDamageBonus: z.number().int().optional(),
    shopDiscountPercent: z.number().int().min(0).max(90).optional(),
  }),
});

const eventEffectSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('hp'), amount: z.number().int() }),
  z.object({ kind: z.literal('iron'), amount: z.number().int() }),
  z.object({ kind: z.literal('max_hp'), amount: z.number().int() }),
  z.object({ kind: z.literal('add_card'), cardDefinitionId: z.string() }),
  z.object({ kind: z.literal('choose_upgrade') }),
  z.object({ kind: z.literal('choose_remove') }),
  z.object({ kind: z.literal('repair_all') }),
  z.object({ kind: z.literal('add_relic'), relicId: z.string() }),
]);

export const eventDefinitionSchema = z.object({
  id: z.string().regex(/^[a-z0-9_]+$/),
  act: z.number().int().min(1).max(3),
  title: z.string().min(1),
  body: z.string().min(1),
  choices: z
    .array(
      z.object({
        id: z.string().regex(/^[a-z0-9_]+$/),
        label: z.string().min(1),
        outcome: z.string().min(1),
        effects: z.array(eventEffectSchema),
      }),
    )
    .min(2)
    .max(3),
});
